import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html, list } from "../lib/bind";
import { bindSelect } from "../lib/formBind";
import { form } from "../lib/form";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type InterestReconciliationOptions,
  type LoanHistoryItem,
  type LoanInput,
  type LoanStatus,
  type LoanSchedule,
  type LoanSummary,
  type LoanType,
  type SchedulePreview,
  type SettlementOutcome,
  createLoan,
  deleteLoan,
  deleteSchedule,
  getInterestReconciliationOptions,
  getLoan,
  getSchedule,
  listLoans,
  previewSchedule,
  previewSettlement,
  reconcileInterest,
  replaceSchedule,
  settleLoan,
  updateLoan,
} from "../services/loan";
import { type BankTransaction, listTransactions } from "../services/transaction";

const LOAN_TYPE_LABELS: Record<LoanType, string> = {
  bank_loan: "Bank loan",
  directors_loan_in: "Director's loan (into the company)",
  other: "Other",
};

function emptyLoanInput() {
  return {
    lender_name: "",
    reference: "",
    loan_type: "bank_loan" as LoanType,
    start_date: "",
    original_principal: "",
    opening_balance: "",
    opening_balance_date: new Date().toISOString().slice(0, 10),
    annual_interest_rate: "",
    term_months: "",
    status: "active" as LoanStatus,
    notes: "",
  };
}

export function loansListView(): Node {
  const loans = signal<LoanSummary[]>([]);
  const loadError = signal("");

  function reload(): void {
    listLoans()
      .then((result) => {
        loans.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reload();

  async function removeLoan(l: LoanSummary): Promise<void> {
    if (!window.confirm(`Delete loan "${l.lender_name}"?`)) return;
    try {
      await deleteLoan(l.id);
      reload();
      toast.success("Loan deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to delete loan");
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <a href="/loans/new" data-link class="btn btn-primary">Add loan</a>
      </div>
      <div class="data-table loans-table">
        <table>
          <thead>
            <tr>
              <th>Lender</th>
              <th>Loan identifier</th>
              <th>Type</th>
              <th>Outstanding balance</th>
              <th>Rate</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="loans-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const loansTable = fragment.querySelector<HTMLTableElement>(".loans-table table");
  const loansBody = fragment.querySelector<HTMLTableSectionElement>(".loans-body");
  if (!errorEl || !loansTable || !loansBody) {
    throw new Error("loans list markup missing expected elements");
  }
  bindErrorText(errorEl, loadError);

  list(
    loansBody,
    loans,
    (l) => l.id,
    (l) => {
      const row = html`<tr>
        <td><a href="/loans/${l.id}" data-link>${l.lender_name}</a></td>
        <td>${l.reference ?? "—"}</td>
        <td>${LOAN_TYPE_LABELS[l.loan_type]}</td>
        <td>${formatMoney(l.outstanding_balance)}</td>
        <td>${l.annual_interest_rate ? `${l.annual_interest_rate}%` : "—"}</td>
        <td><button class="btn btn-danger loan-delete-btn" type="button">Delete</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("loan row template produced no element");
      const deleteBtn = row.querySelector<HTMLButtonElement>(".loan-delete-btn");
      deleteBtn?.addEventListener("click", () => {
        void removeLoan(l);
      });
      return labelRow(row, loansTable);
    },
  );

  return fragment;
}

export function loanDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const loanId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");
  const openingBalance = signal("");
  const outstandingBalance = signal("");
  const history = signal<LoanHistoryItem[]>([]);

  const reconFrom = signal("");
  const reconTo = signal("");
  const reconOptions = signal<InterestReconciliationOptions | null>(null);
  const reconSelectedTxnId = signal("");
  const reconStatementInterest = signal("");
  const reconError = signal("");
  const reconLoading = signal(false);

  const schedule = signal<LoanSchedule | null>(null);
  const schedulePreview = signal<SchedulePreview | null>(null);
  const scheduleFile = signal<File | null>(null);
  const scheduleError = signal("");
  const scheduleBusy = signal(false);

  const settleCandidates = signal<BankTransaction[]>([]);
  const settleTxnId = signal("");
  const settleInterest = signal("");
  const settleAdjustment = signal("");
  const settleNote = signal("");
  const settlePreview = signal<SettlementOutcome | null>(null);
  const settleError = signal("");

  breadcrumb.value = { label: "Loans", path: "/loans" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const loanForm = form(emptyLoanInput());

  function reloadLoanDetail(): void {
    getLoan(paramId)
      .then((loan) => {
        loanForm.reset({
          lender_name: loan.lender_name,
          reference: loan.reference ?? "",
          loan_type: loan.loan_type,
          start_date: loan.start_date ?? "",
          original_principal: loan.original_principal ?? "",
          opening_balance: loan.opening_balance,
          opening_balance_date: loan.opening_balance_date,
          annual_interest_rate: loan.annual_interest_rate ?? "",
          term_months: loan.term_months !== null ? String(loan.term_months) : "",
          status: loan.status,
          notes: loan.notes ?? "",
        });
        savedAt.value = loan.updated_at;
        openingBalance.value = loan.opening_balance;
        outstandingBalance.value = loan.outstanding_balance;
        history.value = loan.history;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  function reloadSchedule(): void {
    getSchedule(paramId)
      .then((result) => {
        schedule.value = result;
      })
      .catch(() => undefined);
  }
  if (!isNew) {
    reloadLoanDetail();
    reloadSchedule();
  }

  async function previewScheduleFile(): Promise<void> {
    const file = scheduleFile.value;
    if (!file) {
      scheduleError.value = "Choose a CSV file first.";
      return;
    }
    scheduleError.value = "";
    scheduleBusy.value = true;
    try {
      schedulePreview.value = await previewSchedule(paramId, file);
    } catch (err) {
      schedulePreview.value = null;
      scheduleError.value = err instanceof ApiRequestError ? err.body.message : "Preview failed";
    } finally {
      scheduleBusy.value = false;
    }
  }

  async function commitScheduleFile(): Promise<void> {
    const file = scheduleFile.value;
    if (!file) {
      scheduleError.value = "Choose a CSV file first.";
      return;
    }
    if (
      schedule.value !== null &&
      schedule.value.entries.length > 0 &&
      !window.confirm("Replace the whole schedule with this file? Existing splits are kept.")
    ) {
      return;
    }
    scheduleError.value = "";
    scheduleBusy.value = true;
    try {
      const result = await replaceSchedule(paramId, file);
      schedulePreview.value = null;
      scheduleFile.value = null;
      const parts = [`${String(result.created_count)} instalment(s) imported`];
      if (result.auto_split_count > 0) {
        parts.push(`${String(result.auto_split_count)} existing payment(s) split automatically`);
      } else if (result.matched_count > 0) {
        parts.push(`${String(result.matched_count)} matched to existing payments`);
      }
      toast.success(`${parts.join("; ")}.`);
      if (result.date_order_ambiguous) {
        toast.warning("Every date could be read either way — imported as day/month. Check them.");
      }
      reloadSchedule();
      reloadLoanDetail();
    } catch (err) {
      scheduleError.value = err instanceof ApiRequestError ? err.body.message : "Import failed";
      toast.error(scheduleError.value);
    } finally {
      scheduleBusy.value = false;
    }
  }

  async function removeSchedule(): Promise<void> {
    if (!window.confirm("Delete this loan's repayment schedule?")) return;
    try {
      await deleteSchedule(paramId);
      toast.success("Schedule deleted.");
      reloadSchedule();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Delete failed");
    }
  }

  function loadSettlementCandidates(): void {
    // Every transaction linked to this loan, not just the categorised ones — a
    // settlement payment matches no instalment, so it is typically linked and nothing
    // more. The interest-reconciliation candidate list would exclude exactly that case.
    listTransactions({ loan_id: paramId, page_size: 200 })
      .then((result) => {
        settleCandidates.value = result.items;
      })
      .catch(() => undefined);
  }
  if (!isNew) loadSettlementCandidates();

  async function refreshSettlementPreview(): Promise<void> {
    settleError.value = "";
    if (!settleTxnId.value) {
      settleError.value = "Pick the settlement payment first.";
      return;
    }
    try {
      const outcome = await previewSettlement(paramId, {
        transaction_id: settleTxnId.value,
        interest_amount: settleInterest.value || "0.00",
      });
      settlePreview.value = outcome;
      // Proposed, not applied — the user accepts or overwrites it before anything is
      // written, because only they have the lender's settlement letter.
      settleAdjustment.value = outcome.residue;
    } catch (err) {
      settlePreview.value = null;
      settleError.value = err instanceof ApiRequestError ? err.body.message : "Preview failed";
    }
  }

  async function submitSettlement(): Promise<void> {
    settleError.value = "";
    if (!settleTxnId.value) {
      settleError.value = "Pick the settlement payment first.";
      return;
    }
    if (settlePreview.value === null) {
      settleError.value = "Preview the settlement before applying it.";
      return;
    }
    if (!window.confirm("Settle this loan? It will be closed and its balance cleared.")) return;
    try {
      const result = await settleLoan(paramId, {
        transaction_id: settleTxnId.value,
        interest_amount: settleInterest.value || "0.00",
        settlement_adjustment: settleAdjustment.value || "0.00",
        settlement_note: settleNote.value || null,
      });
      const superseded = result.outcome.superseded_count;
      toast.success(
        superseded > 0
          ? `Loan settled. ${String(superseded)} remaining instalment(s) marked superseded.`
          : "Loan settled.",
      );
      settlePreview.value = null;
      reloadLoanDetail();
      reloadSchedule();
    } catch (err) {
      settleError.value = err instanceof ApiRequestError ? err.body.message : "Settlement failed";
      toast.error(settleError.value);
    }
  }

  function loadReconciliationCandidates(): void {
    if (!reconFrom.value || !reconTo.value) {
      reconError.value = "Pick a from/to date first.";
      return;
    }
    reconError.value = "";
    reconLoading.value = true;
    getInterestReconciliationOptions(paramId, reconFrom.value, reconTo.value)
      .then((result) => {
        reconOptions.value = result;
        reconSelectedTxnId.value = result.candidates[0]?.transaction_id ?? "";
      })
      .catch((err: unknown) => {
        reconOptions.value = null;
        reconError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      })
      .finally(() => {
        reconLoading.value = false;
      });
  }

  async function submitReconciliation(): Promise<void> {
    reconError.value = "";
    if (!reconSelectedTxnId.value) {
      reconError.value = "No transaction selected.";
      return;
    }
    if (!reconStatementInterest.value.trim()) {
      reconError.value = "Enter the lender's statement interest figure.";
      return;
    }
    try {
      await reconcileInterest(paramId, {
        start_date: reconFrom.value,
        end_date: reconTo.value,
        statement_interest: reconStatementInterest.value,
        transaction_id: reconSelectedTxnId.value,
      });
      toast.success("Interest reconciled.");
      reconOptions.value = null;
      reconStatementInterest.value = "";
      reloadLoanDetail();
    } catch (err) {
      reconError.value = err instanceof ApiRequestError ? err.body.message : "Reconciliation failed";
    }
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    if (!loanForm.fields.lender_name.value.trim()) {
      errorMessage.value = "Enter a lender name.";
      return;
    }
    if (!loanForm.fields.opening_balance.value.trim()) {
      errorMessage.value = "Enter an opening balance.";
      return;
    }
    const reference = loanForm.fields.reference.value.trim();
    if (reference && !/^[A-Za-z0-9]+$/.test(reference)) {
      errorMessage.value = "Loan identifier must be alphanumeric (letters and digits only).";
      return;
    }
    const input: LoanInput = {
      lender_name: loanForm.fields.lender_name.value,
      reference: reference || null,
      loan_type: loanForm.fields.loan_type.value,
      start_date: loanForm.fields.start_date.value || null,
      original_principal: loanForm.fields.original_principal.value || null,
      opening_balance: loanForm.fields.opening_balance.value,
      opening_balance_date: loanForm.fields.opening_balance_date.value,
      annual_interest_rate: loanForm.fields.annual_interest_rate.value || null,
      term_months: loanForm.fields.term_months.value
        ? Number(loanForm.fields.term_months.value)
        : null,
      status: loanForm.fields.status.value,
      notes: loanForm.fields.notes.value || null,
    };
    try {
      if (isNew) {
        const created = await createLoan(input);
        toast.queueSuccess("Loan created.");
        location.assign(`/loans/${created.id}`);
        return;
      }
      const updated = await updateLoan(loanId.value, {
        ...input,
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      outstandingBalance.value = updated.outstanding_balance;
      toast.success("Loan saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this loan?")) return;
    try {
      await deleteLoan(loanId.value);
      toast.queueSuccess("Loan deleted.");
      location.assign("/loans");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to delete loan");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header"><h1>${isNew ? "New loan" : "Edit loan"}</h1></div>
    <p class="form-error"></p>

    <form class="form loan-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="loan-lender">Lender</label>
          <input id="loan-lender" required />
        </div>
        <div class="form-field">
          <label for="loan-reference">Loan identifier</label>
          <input id="loan-reference" placeholder="optional, alphanumeric" />
        </div>
        <div class="form-field">
          <label for="loan-type">Type</label>
          <select id="loan-type">
            <option value="bank_loan">Bank loan</option>
            <option value="directors_loan_in">Director's loan (into the company)</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="loan-start-date">Start date</label>
          <input id="loan-start-date" type="date" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="loan-original-principal">Original principal</label>
          <input id="loan-original-principal" inputmode="decimal" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="loan-status">Status</label>
          <select id="loan-status">
            <option value="active">Active</option>
            <option value="settled">Settled</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="loan-opening-balance">Opening balance</label>
          <input id="loan-opening-balance" inputmode="decimal" required />
        </div>
        <div class="form-field">
          <label for="loan-opening-date">As of</label>
          <input id="loan-opening-date" type="date" required />
        </div>
        <div class="form-field">
          <label for="loan-rate">Annual interest rate (%)</label>
          <input id="loan-rate" inputmode="decimal" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="loan-term">Term (months)</label>
          <input id="loan-term" inputmode="numeric" placeholder="optional" />
        </div>
      </div>
      <div class="form-field">
        <label for="loan-notes">Notes</label>
        <textarea id="loan-notes" rows="3"></textarea>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger delete-btn" type="button">Delete</button>
      </div>
    </form>

    <div class="card loan-detail-card">
      <div class="card-label">Loan detail</div>
      <table class="invoice-totals">
        <tr><td>Opening balance</td><td class="detail-opening"></td></tr>
        <tr class="invoice-total-row"><td>Outstanding balance</td><td class="detail-outstanding"></td></tr>
      </table>
      <div class="data-table loan-history-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Tax group</th>
            </tr>
          </thead>
          <tbody class="history-body"></tbody>
        </table>
      </div>
    </div>

    <div class="card schedule-card">
      <div class="card-label-row">
        <div class="card-label">Repayment schedule</div>
        <button class="btn btn-danger schedule-delete-btn" type="button">Delete schedule</button>
      </div>
      <p class="field-hint">
        Import the lender's own schedule (Date, Principal, Interest, Status). Their figures
        then split each repayment as it is linked to this loan — no arithmetic, no year-end
        correction.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="schedule-file">Lender's CSV</label>
          <input id="schedule-file" type="file" accept=".csv,text/csv" />
        </div>
        <div class="form-actions">
          <button class="btn schedule-preview-btn" type="button">Preview</button>
          <button class="btn btn-primary schedule-import-btn" type="button">
            Replace schedule
          </button>
        </div>
      </div>
      <p class="form-error schedule-error"></p>

      <div class="schedule-preview-panel">
        <p class="schedule-preview-summary"></p>
        <div class="data-table schedule-preview-table">
          <table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Due date</th>
                <th>Interest</th>
                <th>Principal</th>
                <th>Total</th>
                <th>Lender status</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody class="schedule-preview-body"></tbody>
          </table>
        </div>
      </div>

      <p class="schedule-summary"></p>
      <div class="data-table schedule-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Due date</th>
              <th>Interest</th>
              <th>Principal</th>
              <th>Total</th>
              <th>Balance after</th>
              <th>Lender status</th>
              <th>Matched</th>
            </tr>
          </thead>
          <tbody class="schedule-body"></tbody>
        </table>
      </div>
    </div>

    <div class="card settle-card">
      <div class="card-label">Settle loan early</div>
      <p class="field-hint">
        For a lump-sum settlement, which matches no instalment. Enter the lender's interest
        figure if they gave you one; anything left over is stated as a settlement adjustment
        (a rebate of unearned interest, or a fee if negative). The adjustment is a
        balance-sheet correction only — it is never income or an expense.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="settle-txn">Settlement payment</label>
          <select id="settle-txn"></select>
        </div>
        <div class="form-field">
          <label for="settle-interest">Interest per lender</label>
          <input id="settle-interest" inputmode="decimal" placeholder="0.00 if unknown" />
        </div>
        <div class="form-actions">
          <button class="btn settle-preview-btn" type="button">Preview</button>
        </div>
      </div>
      <div class="settle-results">
        <table class="invoice-totals">
          <tr><td>Balance before</td><td class="settle-balance-before"></td></tr>
          <tr><td>Interest portion</td><td class="settle-interest-portion"></td></tr>
          <tr><td>Principal portion</td><td class="settle-principal-portion"></td></tr>
          <tr class="invoice-total-row"><td>Residue</td><td class="settle-residue"></td></tr>
        </table>
        <div class="form-row">
          <div class="form-field">
            <label for="settle-adjustment">Settlement adjustment</label>
            <input id="settle-adjustment" inputmode="decimal" />
            <p class="field-hint">Pre-filled with the residue above. Overwrite if the lender said otherwise.</p>
          </div>
          <div class="form-field">
            <label for="settle-note">Note</label>
            <input id="settle-note" placeholder="e.g. early settlement rebate per letter" />
          </div>
          <div class="form-actions">
            <button class="btn btn-primary settle-submit-btn" type="button">Settle loan</button>
          </div>
        </div>
      </div>
      <p class="form-error settle-error"></p>
    </div>

    <div class="card recon-card">
      <div class="card-label">Year-end interest reconciliation</div>
      <p class="field-hint">
        Compares app-recorded interest against your lender's annual statement for a
        period, and adjusts one repayment's split so the totals match.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="recon-from">From</label>
          <input id="recon-from" type="date" />
        </div>
        <div class="form-field">
          <label for="recon-to">To</label>
          <input id="recon-to" type="date" />
        </div>
        <div class="form-actions">
          <button class="btn recon-load-btn" type="button">Load candidates</button>
        </div>
      </div>
      <div class="form-row recon-results">
        <div class="form-field">
          <span class="card-label">App-recorded interest</span>
          <span class="recon-app-interest"></span>
        </div>
        <div class="form-field">
          <label for="recon-txn">Transaction to adjust</label>
          <select id="recon-txn"></select>
        </div>
        <div class="form-field">
          <label for="recon-statement">Statement interest (per lender)</label>
          <input id="recon-statement" inputmode="decimal" />
        </div>
        <div class="form-actions">
          <button class="btn btn-primary recon-submit-btn" type="button">Reconcile</button>
        </div>
      </div>
      <p class="form-error recon-error"></p>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const lenderInput = fragment.querySelector<HTMLInputElement>("#loan-lender");
  const referenceInput = fragment.querySelector<HTMLInputElement>("#loan-reference");
  const typeSelect = fragment.querySelector<HTMLSelectElement>("#loan-type");
  const startDateInput = fragment.querySelector<HTMLInputElement>("#loan-start-date");
  const originalPrincipalInput = fragment.querySelector<HTMLInputElement>(
    "#loan-original-principal",
  );
  const statusSelect = fragment.querySelector<HTMLSelectElement>("#loan-status");
  const openingBalanceInput = fragment.querySelector<HTMLInputElement>("#loan-opening-balance");
  const openingDateInput = fragment.querySelector<HTMLInputElement>("#loan-opening-date");
  const rateInput = fragment.querySelector<HTMLInputElement>("#loan-rate");
  const termInput = fragment.querySelector<HTMLInputElement>("#loan-term");
  const notesInput = fragment.querySelector<HTMLTextAreaElement>("#loan-notes");
  const deleteBtn = fragment.querySelector<HTMLButtonElement>(".delete-btn");
  const detailCard = fragment.querySelector<HTMLElement>(".loan-detail-card");
  const openingEl = fragment.querySelector<HTMLElement>(".detail-opening");
  const outstandingEl = fragment.querySelector<HTMLElement>(".detail-outstanding");
  const historyTable = fragment.querySelector<HTMLTableElement>(".loan-history-table table");
  const historyBody = fragment.querySelector<HTMLTableSectionElement>(".history-body");
  const scheduleCard = fragment.querySelector<HTMLElement>(".schedule-card");
  const scheduleFileInput = fragment.querySelector<HTMLInputElement>("#schedule-file");
  const schedulePreviewBtn = fragment.querySelector<HTMLButtonElement>(".schedule-preview-btn");
  const scheduleImportBtn = fragment.querySelector<HTMLButtonElement>(".schedule-import-btn");
  const scheduleDeleteBtn = fragment.querySelector<HTMLButtonElement>(".schedule-delete-btn");
  const scheduleErrorEl = fragment.querySelector<HTMLParagraphElement>(".schedule-error");
  const schedulePreviewPanel = fragment.querySelector<HTMLElement>(".schedule-preview-panel");
  const schedulePreviewSummaryEl = fragment.querySelector<HTMLElement>(
    ".schedule-preview-summary",
  );
  const schedulePreviewTable = fragment.querySelector<HTMLTableElement>(
    ".schedule-preview-table table",
  );
  const schedulePreviewBody = fragment.querySelector<HTMLTableSectionElement>(
    ".schedule-preview-body",
  );
  const scheduleSummaryEl = fragment.querySelector<HTMLElement>(".schedule-summary");
  const scheduleTable = fragment.querySelector<HTMLTableElement>(".schedule-table table");
  const scheduleBody = fragment.querySelector<HTMLTableSectionElement>(".schedule-body");
  const settleCard = fragment.querySelector<HTMLElement>(".settle-card");
  const settleTxnSelect = fragment.querySelector<HTMLSelectElement>("#settle-txn");
  const settleInterestInput = fragment.querySelector<HTMLInputElement>("#settle-interest");
  const settlePreviewBtn = fragment.querySelector<HTMLButtonElement>(".settle-preview-btn");
  const settleResults = fragment.querySelector<HTMLElement>(".settle-results");
  const settleBalanceBeforeEl = fragment.querySelector<HTMLElement>(".settle-balance-before");
  const settleInterestPortionEl = fragment.querySelector<HTMLElement>(".settle-interest-portion");
  const settlePrincipalPortionEl = fragment.querySelector<HTMLElement>(
    ".settle-principal-portion",
  );
  const settleResidueEl = fragment.querySelector<HTMLElement>(".settle-residue");
  const settleAdjustmentInput = fragment.querySelector<HTMLInputElement>("#settle-adjustment");
  const settleNoteInput = fragment.querySelector<HTMLInputElement>("#settle-note");
  const settleSubmitBtn = fragment.querySelector<HTMLButtonElement>(".settle-submit-btn");
  const settleErrorEl = fragment.querySelector<HTMLParagraphElement>(".settle-error");
  const reconCard = fragment.querySelector<HTMLElement>(".recon-card");
  const reconFromInput = fragment.querySelector<HTMLInputElement>("#recon-from");
  const reconToInput = fragment.querySelector<HTMLInputElement>("#recon-to");
  const reconLoadBtn = fragment.querySelector<HTMLButtonElement>(".recon-load-btn");
  const reconResults = fragment.querySelector<HTMLElement>(".recon-results");
  const reconAppInterestEl = fragment.querySelector<HTMLElement>(".recon-app-interest");
  const reconTxnSelect = fragment.querySelector<HTMLSelectElement>("#recon-txn");
  const reconStatementInput = fragment.querySelector<HTMLInputElement>("#recon-statement");
  const reconSubmitBtn = fragment.querySelector<HTMLButtonElement>(".recon-submit-btn");
  const reconErrorEl = fragment.querySelector<HTMLParagraphElement>(".recon-error");

  if (
    !errorEl ||
    !lenderInput ||
    !referenceInput ||
    !typeSelect ||
    !startDateInput ||
    !originalPrincipalInput ||
    !statusSelect ||
    !openingBalanceInput ||
    !openingDateInput ||
    !rateInput ||
    !termInput ||
    !notesInput ||
    !deleteBtn ||
    !detailCard ||
    !openingEl ||
    !outstandingEl ||
    !historyTable ||
    !historyBody ||
    !scheduleCard ||
    !scheduleFileInput ||
    !schedulePreviewBtn ||
    !scheduleImportBtn ||
    !scheduleDeleteBtn ||
    !scheduleErrorEl ||
    !schedulePreviewPanel ||
    !schedulePreviewSummaryEl ||
    !schedulePreviewTable ||
    !schedulePreviewBody ||
    !scheduleSummaryEl ||
    !scheduleTable ||
    !scheduleBody ||
    !settleCard ||
    !settleTxnSelect ||
    !settleInterestInput ||
    !settlePreviewBtn ||
    !settleResults ||
    !settleBalanceBeforeEl ||
    !settleInterestPortionEl ||
    !settlePrincipalPortionEl ||
    !settleResidueEl ||
    !settleAdjustmentInput ||
    !settleNoteInput ||
    !settleSubmitBtn ||
    !settleErrorEl ||
    !reconCard ||
    !reconFromInput ||
    !reconToInput ||
    !reconLoadBtn ||
    !reconResults ||
    !reconAppInterestEl ||
    !reconTxnSelect ||
    !reconStatementInput ||
    !reconSubmitBtn ||
    !reconErrorEl
  ) {
    throw new Error("loan detail markup missing expected elements");
  }
  if (isNew) {
    deleteBtn.style.display = "none";
    detailCard.style.display = "none";
    reconCard.style.display = "none";
    scheduleCard.style.display = "none";
    settleCard.style.display = "none";
  }

  bindErrorText(errorEl, errorMessage);
  bindValue(lenderInput, loanForm.fields.lender_name);
  bindValue(referenceInput, loanForm.fields.reference);
  bindSelect(typeSelect, loanForm.fields.loan_type);
  bindValue(startDateInput, loanForm.fields.start_date);
  bindValue(originalPrincipalInput, loanForm.fields.original_principal);
  bindSelect(statusSelect, loanForm.fields.status);
  bindValue(openingBalanceInput, loanForm.fields.opening_balance);
  bindValue(openingDateInput, loanForm.fields.opening_balance_date);
  bindValue(rateInput, loanForm.fields.annual_interest_rate);
  bindValue(termInput, loanForm.fields.term_months);
  bindValue(notesInput, loanForm.fields.notes);
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  bindErrorText(scheduleErrorEl, scheduleError);
  scheduleFileInput.addEventListener("change", () => {
    scheduleFile.value = scheduleFileInput.files?.[0] ?? null;
    schedulePreview.value = null;
  });
  schedulePreviewBtn.addEventListener("click", () => {
    void previewScheduleFile();
  });
  scheduleImportBtn.addEventListener("click", () => {
    void commitScheduleFile();
  });
  scheduleDeleteBtn.addEventListener("click", () => {
    void removeSchedule();
  });
  currentScope().effect(() => {
    const busy = scheduleBusy.value;
    schedulePreviewBtn.disabled = busy;
    scheduleImportBtn.disabled = busy;
    scheduleDeleteBtn.style.display =
      (schedule.value?.entries.length ?? 0) > 0 ? "" : "none";
  });
  bindShow(
    schedulePreviewPanel,
    computed(() => schedulePreview.value !== null),
  );
  currentScope().effect(() => {
    const preview = schedulePreview.value;
    if (!preview) {
      schedulePreviewSummaryEl.textContent = "";
      return;
    }
    const parts = [
      `${String(preview.valid_count)} instalment(s) readable`,
      `${String(preview.settled_count)} marked settled by the lender`,
    ];
    if (preview.error_count > 0) parts.push(`${String(preview.error_count)} unreadable`);
    if (preview.skipped_rows.length > 0) {
      parts.push(`skipped: ${preview.skipped_rows.join(", ")}`);
    }
    if (preview.date_order_ambiguous) {
      parts.push("every date reads either way — assumed day/month");
    }
    schedulePreviewSummaryEl.textContent = `${parts.join(" · ")}.`;
  });
  list(
    schedulePreviewBody,
    computed(() => schedulePreview.value?.rows ?? []),
    (row) => String(row.row_number),
    (row) => {
      const el = html`<tr>
        <td>${String(row.row_number)}</td>
        <td>${row.due_date ? formatDate(row.due_date) : "—"}</td>
        <td>${row.interest ? formatMoney(row.interest) : "—"}</td>
        <td>${row.principal ? formatMoney(row.principal) : "—"}</td>
        <td>${row.total ? formatMoney(row.total) : "—"}</td>
        <td>${row.lender_status ?? "—"}</td>
        <td>${row.errors.join("; ")}</td>
      </tr>`.firstElementChild;
      if (!el) throw new Error("schedule preview row template produced no element");
      if (row.errors.length > 0) el.classList.add("preview-row-error");
      return labelRow(el, schedulePreviewTable);
    },
  );

  currentScope().effect(() => {
    const current = schedule.value;
    if (!current || current.entries.length === 0) {
      scheduleSummaryEl.textContent = "No schedule loaded.";
      return;
    }
    const s = current.summary;
    const parts = [
      `${String(s.instalment_count)} instalment(s)`,
      `${String(s.matched_count)} matched to bank payments`,
      `${String(s.settled_per_lender_count)} settled per the lender`,
    ];
    if (s.superseded_count > 0) parts.push(`${String(s.superseded_count)} superseded`);
    parts.push(`${formatMoney(s.scheduled_principal_remaining)} principal still scheduled`);
    parts.push(`balance ${formatMoney(s.outstanding_balance)}`);
    scheduleSummaryEl.textContent = `${parts.join(" · ")}.`;
  });
  list(
    scheduleBody,
    computed(() => schedule.value?.entries ?? []),
    (entry) => entry.id,
    (entry) => {
      const el = html`<tr>
        <td>${String(entry.instalment_number)}</td>
        <td>${formatDate(entry.due_date)}</td>
        <td>${formatMoney(entry.interest_amount)}</td>
        <td>${formatMoney(entry.principal_amount)}</td>
        <td>${formatMoney(entry.total_amount)}</td>
        <td>${formatMoney(entry.balance_after)}</td>
        <td>${entry.lender_status ?? "—"}</td>
        <td>${entry.matched_transaction_id ? "Yes" : "—"}</td>
      </tr>`.firstElementChild;
      if (!el) throw new Error("schedule row template produced no element");
      if (entry.superseded) el.classList.add("schedule-row-superseded");
      return labelRow(el, scheduleTable);
    },
  );

  bindErrorText(settleErrorEl, settleError);
  bindValue(settleInterestInput, settleInterest);
  bindValue(settleAdjustmentInput, settleAdjustment);
  bindValue(settleNoteInput, settleNote);
  settleTxnSelect.addEventListener("change", () => {
    settleTxnId.value = settleTxnSelect.value;
    settlePreview.value = null;
  });
  settlePreviewBtn.addEventListener("click", () => {
    void refreshSettlementPreview();
  });
  settleSubmitBtn.addEventListener("click", () => {
    void submitSettlement();
  });
  bindShow(
    settleResults,
    computed(() => settlePreview.value !== null),
  );
  currentScope().effect(() => {
    settleTxnSelect.replaceChildren();
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "(pick a payment)";
    settleTxnSelect.appendChild(blank);
    for (const candidate of settleCandidates.value) {
      const opt = document.createElement("option");
      opt.value = candidate.id;
      opt.textContent = `${formatDate(candidate.date)} — ${candidate.description} (${formatMoney(candidate.amount)})`;
      settleTxnSelect.appendChild(opt);
    }
    settleTxnSelect.value = settleTxnId.value;
  });
  currentScope().effect(() => {
    const outcome = settlePreview.value;
    settleBalanceBeforeEl.textContent = outcome ? formatMoney(outcome.balance_before) : "";
    settleInterestPortionEl.textContent = outcome ? formatMoney(outcome.interest_amount) : "";
    settlePrincipalPortionEl.textContent = outcome ? formatMoney(outcome.principal_amount) : "";
    settleResidueEl.textContent = outcome ? formatMoney(outcome.residue) : "";
  });

  bindErrorText(reconErrorEl, reconError);
  bindShow(
    reconResults,
    computed(() => reconOptions.value !== null),
  );
  reconFromInput.addEventListener("change", () => {
    reconFrom.value = reconFromInput.value;
  });
  reconToInput.addEventListener("change", () => {
    reconTo.value = reconToInput.value;
  });
  reconLoadBtn.addEventListener("click", loadReconciliationCandidates);
  currentScope().effect(() => {
    reconLoadBtn.disabled = reconLoading.value;
    reconLoadBtn.textContent = reconLoading.value ? "Loading…" : "Load candidates";
  });
  reconTxnSelect.addEventListener("change", () => {
    reconSelectedTxnId.value = reconTxnSelect.value;
  });
  bindValue(reconStatementInput, reconStatementInterest);
  reconSubmitBtn.addEventListener("click", () => {
    void submitReconciliation();
  });

  currentScope().effect(() => {
    const options = reconOptions.value;
    reconAppInterestEl.textContent = options ? formatMoney(options.app_recorded_interest) : "";
    reconTxnSelect.replaceChildren();
    if (!options) return;
    for (const candidate of options.candidates) {
      const opt = document.createElement("option");
      opt.value = candidate.transaction_id;
      opt.textContent =
        `${formatDate(candidate.date)} — ${candidate.description} (${formatMoney(candidate.amount)})`;
      reconTxnSelect.appendChild(opt);
    }
    reconTxnSelect.value = reconSelectedTxnId.value;
  });

  currentScope().effect(() => {
    openingEl.textContent = formatMoney(openingBalance.value);
    outstandingEl.textContent = formatMoney(outstandingBalance.value);
  });

  list(
    historyBody,
    history,
    (h) => `${h.transaction_id}-${h.split_id ?? "plain"}`,
    (h) => {
      const row = html`<tr>
        <td>${formatDate(h.date)}</td>
        <td>${h.description}</td>
        <td>${formatMoney(h.amount)}</td>
        <td>${h.tax_group}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("loan history row template produced no element");
      return labelRow(row, historyTable);
    },
  );

  return fragment;
}
